import React, { useMemo, useState } from 'react';
import {
  Filter, ChevronDown, ChevronUp, Download,
} from 'lucide-react';
import { exportToExcel } from '../../utils/excelExport';
import { useAuth } from '../../contexts/AuthContext';
import SortableHeader, { usePagedSortableData, TablePager } from '../SortableTable';
import { formatDate, formatDateTime } from '../../utils/dateFormat';
import { fmtDuration, fmtPercent, QuizVerdictBadge } from './quizFormat';
import { exportExcelBtnCls } from '../shared/ExportExcelButton';

/**
 * Presentational — the Quiz Results sub-tab of Participation Records. One
 * row per person per session (the latest graded sitting, with how many
 * sittings it took). Rows arrive already run through the container's
 * shared filters (search / session / date range); this keeps only what quiz
 * results have of their own: the department filter, the per-session
 * grouping with its averages, and the Excel export. Same shape as
 * TrainingFeedback.
 */

const avg = (values) => {
  const list = values.filter((v) => v != null);
  if (list.length === 0) return null;
  return list.reduce((a, b) => a + Number(b), 0) / list.length;
};

const th = 'px-4 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap';
const td = 'px-4 py-3 text-sm text-slate-700 whitespace-nowrap';

function SessionCard({ responses }) {
  const [expanded, setExpanded] = useState(false);
  const first = responses[0];
  const avgPercent = avg(responses.map((r) => r.percent));
  const withVerdict = responses.filter((r) => r.passed != null);
  const passRate = withVerdict.length > 0
    ? (withVerdict.filter((r) => r.passed).length / withVerdict.length) * 100 : null;
  const avgTime = avg(responses.map((r) => r.durationSeconds));
  const t = usePagedSortableData(responses, { key: 'name', direction: 'asc' });

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <button
        className="w-full text-left px-5 py-4 flex items-start justify-between hover:bg-slate-50 transition-colors"
        onClick={() => setExpanded((e) => !e)}
      >
        <div className="flex-1 min-w-0 pr-4">
          <h3 className="font-semibold text-slate-800 leading-snug">{first.courseTitle}</h3>
          <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-slate-500">
            {first.date && <span>{formatDate(first.date)}</span>}
            <span className="text-slate-600">{first.quizTitle}</span>
            <span className="text-slate-600">{responses.length} result{responses.length !== 1 ? 's' : ''}</span>
          </div>
        </div>
        <div className="flex items-center gap-5 shrink-0">
          <div className="text-center">
            <div className="text-xs text-slate-600">Avg Score</div>
            <div className="text-sm font-bold text-blue-700">{fmtPercent(avgPercent)}</div>
          </div>
          {passRate != null && (
            <div className="text-center">
              <div className="text-xs text-slate-600">Pass Rate</div>
              <div className={`text-sm font-bold ${passRate >= 100 ? 'text-emerald-700' : 'text-amber-700'}`}>{Math.round(passRate)}%</div>
            </div>
          )}
          <div className="text-center">
            <div className="text-xs text-slate-600">Avg Time</div>
            <div className="text-sm font-bold text-slate-700">{fmtDuration(avgTime)}</div>
          </div>
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-100 table-scroll">
          <table className="w-full text-left">
            <thead className="bg-slate-50">
              <tr className="bg-slate-50">
                <SortableHeader label="Staff" sortKey="name" sortConfig={t.sortConfig} onSort={t.requestSort} className={th} />
                <SortableHeader label="Designation" sortKey="position" sortConfig={t.sortConfig} onSort={t.requestSort} className={th} />
                <SortableHeader label="Dept" sortKey="department" sortConfig={t.sortConfig} onSort={t.requestSort} className={th} />
                <SortableHeader label="Attempts" sortKey="attempts" sortConfig={t.sortConfig} onSort={t.requestSort} align="right" className={th} />
                <SortableHeader label="Score" sortKey="score" sortConfig={t.sortConfig} onSort={t.requestSort} align="right" className={th} />
                <SortableHeader label="%" sortKey="percent" sortConfig={t.sortConfig} onSort={t.requestSort} align="right" className={th} />
                <SortableHeader label="Result" sortKey="passed" sortConfig={t.sortConfig} onSort={t.requestSort} accessor={(r) => (r.passed == null ? -1 : (r.passed ? 1 : 0))} className={th} />
                <SortableHeader label="Time Taken" sortKey="durationSeconds" sortConfig={t.sortConfig} onSort={t.requestSort} align="right" className={th} />
                <SortableHeader label="Focus Lost" sortKey="violations" sortConfig={t.sortConfig} onSort={t.requestSort} align="right" className={th} />
                <SortableHeader label="Submitted" sortKey="submittedAt" sortConfig={t.sortConfig} onSort={t.requestSort} className={th} />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {t.rows.map((r, i) => (
                <tr key={`${r.email}-${i}`} className="hover:bg-slate-50">
                  <td className={td}>
                    <div className="font-medium text-slate-900">{r.name}</div>
                    <div className="text-xs text-slate-500">{r.email}</div>
                  </td>
                  <td className={`${td} text-slate-500`}>{r.position || '—'}</td>
                  <td className={`${td} text-slate-500`}>{r.department || '—'}</td>
                  <td className={`${td} text-right tabular-nums`}>{r.attempts}</td>
                  <td className={`${td} text-right tabular-nums`}>{r.score} / {r.maxScore}</td>
                  <td className={`${td} text-right tabular-nums font-semibold`}>{fmtPercent(r.percent)}</td>
                  <td className={td}><QuizVerdictBadge passed={r.passed} /></td>
                  <td className={`${td} text-right tabular-nums`}>{fmtDuration(r.durationSeconds)}</td>
                  <td className={`${td} text-right tabular-nums`}>
                    {r.violations > 0
                      ? <span className="text-amber-700">{r.violations}</span>
                      : <span className="text-slate-400">0</span>}
                  </td>
                  <td className={`${td} text-slate-500`}>{formatDateTime(r.submittedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <TablePager {...t} className="flex items-center justify-between px-4 py-2 border-t border-gray-100 bg-slate-50" />
        </div>
      )}
    </div>
  );
}

const QuizResults = ({ records }) => {
  const [filterDepartment, setFilterDepartment] = useState('all');
  const { hasPermission } = useAuth();

  const uniqueDepartments = useMemo(
    () => ['all', ...new Set(records.map((r) => r.department).filter(Boolean).sort())],
    [records]
  );
  const filtered = useMemo(
    () => (filterDepartment === 'all' ? records : records.filter((r) => r.department === filterDepartment)),
    [records, filterDepartment]
  );

  // Group per session (session code), newest session first.
  const sessions = useMemo(() => {
    const groups = {};
    filtered.forEach((r) => {
      const key = r.sessionId || r.sessionCode;
      if (!groups[key]) groups[key] = [];
      groups[key].push(r);
    });
    return Object.entries(groups).sort(([, a], [, b]) => (b[0].date || '').localeCompare(a[0].date || ''));
  }, [filtered]);

  const stats = useMemo(() => {
    const withVerdict = filtered.filter((r) => r.passed != null);
    return {
      total: filtered.length,
      unique: new Set(filtered.map((r) => r.email)).size,
      avgPercent: avg(filtered.map((r) => r.percent)),
      passRate: withVerdict.length > 0
        ? Math.round((withVerdict.filter((r) => r.passed).length / withVerdict.length) * 100) : null,
      avgTime: avg(filtered.map((r) => r.durationSeconds)),
    };
  }, [filtered]);

  const handleExport = () => {
    const rows = filtered.map((r) => ({
      'Training Session': r.courseTitle,
      'Date': formatDate(r.date),
      'Quiz': r.quizTitle,
      'Name': r.name,
      'Email': r.email,
      'Designation': r.position || '',
      'Department': r.department || '',
      'Attempts': r.attempts,
      'Score': r.score,
      'Max Score': r.maxScore,
      'Percent': r.percent,
      'Result': r.passed == null ? '' : (r.passed ? 'Passed' : 'Not passed'),
      'Time Taken': fmtDuration(r.durationSeconds),
      'Focus Lost': r.violations,
      'Submitted': formatDateTime(r.submittedAt),
    }));
    exportToExcel(rows, 'Quiz_Results_Report.xlsx');
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <p className="text-sm text-gray-500">Results</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{stats.total}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <p className="text-sm text-gray-500">People</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{stats.unique}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <p className="text-sm text-gray-500">Avg Score</p>
          <p className="text-3xl font-bold text-blue-700 mt-1">{fmtPercent(stats.avgPercent)}</p>
          {stats.passRate != null && (
            <p className="text-xs text-gray-500 mt-1">Pass rate {stats.passRate}% (where a pass mark is set)</p>
          )}
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <p className="text-sm text-gray-500">Avg Time Taken</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{fmtDuration(stats.avgTime)}</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="relative w-full sm:w-72">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <select
              value={filterDepartment}
              onChange={(e) => setFilterDepartment(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 appearance-none bg-white"
            >
              {uniqueDepartments.map((d) => (
                <option key={d} value={d}>{d === 'all' ? 'All Departments' : d}</option>
              ))}
            </select>
          </div>
          {hasPermission('ld:view') && (
            <button
              onClick={handleExport}
              disabled={filtered.length === 0}
              className={exportExcelBtnCls}
            >
              <Download className="w-4 h-4" />
              Export Excel
            </button>
          )}
        </div>
        {filterDepartment !== 'all' && (
          <div className="mt-3 flex items-center justify-between">
            <span className="text-sm text-gray-600">Showing {filtered.length} of {records.length} results</span>
            <button onClick={() => setFilterDepartment('all')} className="text-sm text-blue-600 hover:text-blue-800 font-medium">
              Clear Department Filter
            </button>
          </div>
        )}
      </div>

      {sessions.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-10 text-center text-gray-500">
          No quiz results found.
        </div>
      ) : (
        <div className="space-y-3">
          {sessions.map(([key, responses]) => <SessionCard key={key} responses={responses} />)}
        </div>
      )}
    </div>
  );
};

export default QuizResults;
